import { GetOptions } from "@/core";
import { useMemo } from "react";
import { useMonthlyReportsPaginated } from "./repository-hooks/report/use-reports";

/**
 * Custom hook to extract available years from monthly reports data
 * Returns years in descending order (newest first) for better UX
 */
export const useAvailableYears = () => {
  // Fetch minimal page to gather year set; we can still compute years from first pages
  const options: GetOptions = {
    pagination: { limit: 50 },
    orderBy: { field: "dataAsOf", direction: "desc" },
  };
  const monthlyData = useMonthlyReportsPaginated(options);
  const reports = useMemo(
    () => (monthlyData.data ? monthlyData.data.pages.flat() : []),
    [monthlyData.data],
  );

  const availableYears = useMemo(() => {
    if (!reports || reports.length === 0) {
      return [];
    }

    // Extract unique years from reports
    const yearsSet = new Set<number>();
    reports.forEach((report) => {
      yearsSet.add(report.year);
    });

    // Convert to array and sort in descending order (newest first)
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [reports]);

  return {
    availableYears,
    isLoading: monthlyData.isLoading,
    error: monthlyData.error,
    hasYears: availableYears.length > 0,
  };
};
